import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { Plus, RefreshCw, Search, Briefcase, Clock, DollarSign, ChevronRight } from 'lucide-react';
import PageHeader from '../../../layouts/PageHeader';
import Select from 'react-select';
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import EmptyState from '../../Common/EmptyState';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { useListControls } from '../../../../hooks/useListControls';
import { useUrlState } from '../../../../hooks/useUrlState';
import ProjectModal from './ProjectModal';
import * as api from '../../../../services/api/projectsApi';
import * as S from '../../Common/internalToolsStyles';
import { useCapabilities } from '../../../../context/CapabilityContext';


// Values come from the project model; 'client' is a project_type, the two
// below are the billing models actually stored.
const BILLING_OPTIONS = [
  { value: 'all', label: 'All debitering' },
  { value: 'time_materials', label: 'Löpande räkning' },
  { value: 'non_billable', label: 'Ej debiterbart' },
];

const ProjectsPage = () => {
  const { companyId } = useParams();
  const history = useHistory();
  // Capability×scope gating per §4.4. The list itself is scope-filtered
  // server-side (admin tier sees all; everyone else sees managed ∪ member
  // ∪ open) so the page can render uniformly — but the "Nytt projekt" CTA
  // is Admin/Owner only. Fail-closed: `hasScope` returns false while
  // capabilities are still loading, so the button doesn't flash for
  // non-eligible users on first paint.
  const { hasScope } = useCapabilities();
  const canCreateProject = hasScope('project', 'create', 'all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // Search, lifecycle and billing model in the URL so a filtered project list
  // is linkable. `lifecycle` defaults to 'active' — the clean URL opens on
  // live projects, not the archive.
  const {
    search,
    setSearch,
    debouncedSearch,
    filter: lifecycleFilter,
    setFilter: setLifecycleFilter,
  } = useListControls({ defaultFilter: 'active', defaultSort: null, filterKey: 'lifecycle' });
  const [billingFilter, setBillingFilter] = useUrlState('debitering', 'all');

  const fetchProjects = useCallback(async () => {
    try {
      if (!loading) setRefreshing(true);
      setError(null);
      const res = await api.getProjects(companyId);
      setProjects(res.data.projects || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte hämta projekt');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleSave = async (data) => {
    const res = await api.createProject(companyId, data);
    setShowModal(false);
    // Backend returns { status, projectId, rateCardId, articleId, message }
    // — flat camelCase. Send the user straight to the full edit page so
    // they can complete the remaining fields (code, budget, team, …)
    // while the create context is still fresh. `?setup=1` flags the page
    // as a setup-completion view rather than a regular edit.
    const newId = res?.data?.projectId || res?.data?.project_id;
    if (newId) {
      history.push(`/${companyId}/projects/${newId}/edit?setup=1`);
    } else {
      await fetchProjects();
    }
  };

  // A project is "setup-incomplete" when it's a just-created stub: an
  // hourly-billable project with no rate set, or no customer on a client
  // project. Surfaces a badge so stubs don't rot (UX reviewer call-out).
  const isIncomplete = (p) => {
    if (p.billing_model === 'time_materials' && !p.hourly_rate) return true;
    if (p.project_type === 'client' && !p.customer_id) return true;
    return false;
  };

  const lifecycleFiltered = useMemo(() => {
    if (lifecycleFilter === 'active') return projects.filter((p) => p.is_active !== false && p.is_active !== 0);
    if (lifecycleFilter === 'archived') return projects.filter((p) => p.is_active === false || p.is_active === 0);
    return projects;
  }, [projects, lifecycleFilter]);

  const counts = useMemo(() => ({
    active:   projects.filter((p) => p.is_active !== false && p.is_active !== 0).length,
    archived: projects.filter((p) => p.is_active === false || p.is_active === 0).length,
    all:      projects.length,
  }), [projects]);

  const filtered = useMemo(() => {
    let pool = lifecycleFiltered;
    if (billingFilter !== 'all') pool = pool.filter((p) => p.billing_model === billingFilter);
    if (!debouncedSearch) return pool;
    const s = debouncedSearch.toLowerCase();
    return pool.filter(p =>
      p.name?.toLowerCase().includes(s) ||
      p.code?.toLowerCase().includes(s) ||
      p.customer_name?.toLowerCase().includes(s)
    );
  }, [lifecycleFiltered, debouncedSearch, billingFilter]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        icon={Briefcase}
        title="Projekt"
        subtitle="Följ tid, kostnader och resultat per projekt."
      />
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Controls render whether or not there are projects — the empty state
          replaces the list, not the page. */}
      <>
          <ListToolbar
            className="mb-3"
            search={{
              value: search,
              onChange: setSearch,
              placeholder: 'Sök projekt, kod eller kund…',
              ariaLabel: 'Sök bland projekt',
            }}
            actions={
              <>
                <button onClick={() => { setRefreshing(true); fetchProjects(); }} disabled={refreshing} style={S.btnIcon}>
                  <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                </button>
                {canCreateProject && (
                  <button onClick={() => setShowModal(true)} style={S.btnPrimary}>
                    <Plus size={14} /> Nytt projekt
                  </button>
                )}
              </>
            }
          >
            <div style={{ minWidth: 190 }}>
              <Select
                options={BILLING_OPTIONS}
                value={BILLING_OPTIONS.find((o) => o.value === billingFilter) || BILLING_OPTIONS[0]}
                onChange={(opt) => setBillingFilter(opt?.value || 'all')}
                isSearchable={false}
                theme={brandSelectTheme}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  control: (b) => ({ ...b, minHeight: 38, fontSize: 13 }),
                  option: (b) => ({ ...b, fontSize: 13 }),
                  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                }}
                aria-label="Filtrera på debitering"
              />
            </div>
          </ListToolbar>

          <FilterChips
            className="mb-3"
            chips={[
              { key: 'active', label: 'Aktiva', count: counts.active },
              { key: 'archived', label: 'Arkiverade', count: counts.archived },
              { key: 'all', label: 'Alla', count: counts.all },
            ]}
            value={lifecycleFilter}
            onChange={setLifecycleFilter}
          />
      </>

      {/* Table */}
      {projects.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="Inga projekt ännu"
          description={canCreateProject
            ? 'Skapa ditt första projekt för att börja rapportera tid och fakturera dina kunder.'
            : 'Du är inte medlem i några projekt än. Be en administratör att lägga till dig.'}
          actions={canCreateProject && (
            <button onClick={() => setShowModal(true)} style={S.btnPrimary}>
              <Plus size={14} /> Skapa projekt
            </button>
          )}
        />
      ) : (
        <div style={S.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr style={S.tableHeaderRow}>
                  <th style={{ ...S.tableHeaderCell, minWidth: 220 }}>PROJEKT</th>
                  <th style={S.tableHeaderCell}>KUND</th>
                  <th style={S.tableHeaderCell}>MODELL</th>
                  <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>TIMPRIS</th>
                  <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>BUDGET</th>
                  <th style={{ ...S.tableHeaderCell, width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(project => {
                  const rate = project.hourly_rate ? Number(project.hourly_rate) : null;

                  return (
                    <tr
                      key={project.project_id}
                      style={S.tableRow}
                      onClick={() => history.push(`/${companyId}/projects/${project.project_id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = S.colors.bgSubtle}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={S.tableCell}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: S.colors.textPrimary }}>{project.name}</div>
                          {isIncomplete(project) && (
                            <span className="proj-incomplete-badge" title="Projektet saknar uppgifter — öppna för att slutföra">
                              Ofullständig
                            </span>
                          )}
                        </div>
                        {project.code && <div style={{ fontSize: 11, color: S.colors.textLight }}>{project.code}</div>}
                      </td>
                      <td style={S.tableCell}>
                        {project.customer_name ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: S.colors.textMuted, fontSize: 13 }}>
                            <Briefcase size={12} /> {project.customer_name}
                          </span>
                        ) : (
                          <span style={{ color: S.colors.textDisabled, fontSize: 13 }}>–</span>
                        )}
                      </td>
                      <td style={S.tableCell}>
                        <span style={S.billingBadge[project.billing_model] || S.billingBadge.non_billable}>
                          {S.billingLabel[project.billing_model] || S.billingLabel.non_billable}
                        </span>
                      </td>
                      <td style={{ ...S.tableCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {rate ? (
                          <span>
                            <span style={{ fontWeight: 600, color: S.colors.textPrimary }}>{rate.toLocaleString('sv-SE')}</span>
                            <span style={{ fontSize: 11, color: S.colors.textLight, marginLeft: 2 }}>{project.currency || 'SEK'}/h</span>
                          </span>
                        ) : (
                          <span style={{ color: S.colors.textDisabled }}>–</span>
                        )}
                      </td>
                      <td style={{ ...S.tableCell, textAlign: 'right' }}>
                        {project.budget_hours ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: S.colors.textMuted, fontSize: 13 }}>
                            <Clock size={12} /> {project.budget_hours}h
                          </span>
                        ) : (
                          <span style={{ color: S.colors.textDisabled }}>–</span>
                        )}
                      </td>
                      <td style={{ ...S.tableCell, padding: '8px 12px' }}>
                        <ChevronRight size={14} style={{ color: S.colors.textDisabled }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filtered.length === 0 && search && (
              <EmptyState variant="no-matches" query={search} />
            )}
          </div>
        </div>
      )}

      <ProjectModal show={showModal} onHide={() => setShowModal(false)} onSave={handleSave} project={null} companyId={companyId} />

      <style>{`
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border: 1px solid #d1d5db; background: white;
          border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
          color: #111827; cursor: pointer; transition: all 0.15s;
        }
          background: var(--rgba-primary-2); color: var(--bs-primary);
          border-color: var(--bs-primary); font-weight: 600;
        }
          font-size: 0.6875rem; padding: 1px 6px; border-radius: 999px;
          background: #e5e7eb; color: #374151; font-weight: 600;
        }

        .proj-incomplete-badge {
          display: inline-block; padding: 1px 7px; border-radius: 999px;
          background: #fef3c7; color: #92400e;
          font-size: 0.625rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.02em;
        }
      `}</style>
    </>
  );
};

export default ProjectsPage;
